import React, { useState, useEffect } from 'react';
import { ArrowRight, Check, Zap, Clock, Shield, Brain, Star, Download, FileAudio, Sparkles, ShieldCheck, Lock, Server, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import PromoBanner from '../components/PromoBanner';
import { Button, Card } from '../components/ui';
import type { User } from '../types';

interface HomePageProps {
  user: User | null;
}

const HomePage: React.FC<HomePageProps> = ({ user }) => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  // ログイン状態でヘッダーの認証導線（PCナビ・SP用ボタン）を切り替える。表示切替のみで認証ロジックには非依存。
  const isLoggedIn = Boolean(user);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-fade-in').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleNavClick = (href: string) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <>
      {/* ヘッダー */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white shadow-sm ${
          scrollY > 50 ? 'py-3' : 'py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/images/logo_icon.png" alt="otomochi" className="w-9 h-9 md:w-11 md:h-11 rounded-[12px] md:rounded-[14px]" />
            <span className="text-xl md:text-[26px] font-bold text-primary">otomochi</span>
          </div>

          <nav className="hidden md:flex items-center gap-9">
            <button onClick={() => handleNavClick('#features')} className="text-text-sub font-medium text-[15px] hover:text-primary transition-colors">
              機能
            </button>
            <button onClick={() => handleNavClick('#pricing')} className="text-text-sub font-medium text-[15px] hover:text-primary transition-colors">
              料金
            </button>
            <button onClick={() => handleNavClick('#about')} className="text-text-sub font-medium text-[15px] hover:text-primary transition-colors">
              サービス
            </button>
            {isLoggedIn ? (
              // ログイン済み: 認証CTA2つ（ログイン＋無料で始める）を単一「ダッシュボードへ」に集約。
              <Button variant="primary" onClick={() => navigate('/dashboard')}>
                ダッシュボードへ
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={() => navigate('/login')}>
                  ログイン
                </Button>
                <Button variant="primary" onClick={() => navigate('/login')}>
                  無料で始める
                </Button>
              </>
            )}
          </nav>

          {/* SP幅（md:未満）専用の導線。PCは上記 nav が担うため md:hidden で二重表示を防ぐ。
              ログイン状態でラベル/遷移先を切り替える（未ログイン=「ログイン」→/login、ログイン済み=「ダッシュボードへ」→/dashboard）。
              data-testid は SP 導線ボタンを指す安定セレクタ（可視ラベルとは独立し、ログイン状態に依らず維持）。 */}
          <Button
            variant="secondary"
            size="sm"
            className="md:hidden whitespace-nowrap"
            data-testid="home-sp-login"
            onClick={() => navigate(isLoggedIn ? '/dashboard' : '/login')}
          >
            {isLoggedIn ? 'ダッシュボードへ' : 'ログイン'}
          </Button>
        </div>
      </header>

      {/* ヒーローセクション */}
      <section className="min-h-screen flex flex-col items-center justify-center pt-[120px] pb-[120px] px-6 relative">
        <div className="max-w-4xl text-center">
          <img
            src="/images/logo.png"
            alt="otomochi"
            width={500}
            height={273}
            fetchPriority="high"
            className="w-[300px] md:w-[420px] max-w-full h-auto rounded-3xl mx-auto mb-6 logo-float"
          />

          <h1 className="text-[clamp(44px,7vw,76px)] font-extrabold leading-[1.15] mt-4 mb-5 tracking-tight">
            <span className="block text-text-heading">TRPGの声を</span>
            <span className="block text-primary">テキストで<br className="md:hidden" />残そう</span>
          </h1>

          <p className="text-xl text-text-sub leading-relaxed mb-7 max-w-2xl mx-auto">
            <span className="block">日本語の音声のみ対応しています（Japanese audio only）</span>
            録音をアップロードするだけで、TRPGセッションを<br />
            タイムスタンプ付きテキストに書き起こします。
          </p>

          <div className="flex items-center justify-center gap-6 flex-wrap mb-10">
            <Button
              size="lg"
              onClick={() => navigate(isLoggedIn ? '/dashboard' : '/login')}
              className="hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
            >
              <span>{isLoggedIn ? 'ダッシュボードへ' : '無料で始める'}</span>
              <ArrowRight className="w-5 h-5" />
            </Button>
            {!isLoggedIn && (
              <div className="flex items-center gap-2.5 px-6 py-3.5 bg-white rounded-[14px] shadow-md text-text font-medium text-[15px]">
                <Check className="w-5 h-5 text-emerald-500" />
                <span>月3回まで無料</span>
              </div>
            )}
          </div>
        </div>

        {/* スクロールインジケーター */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1">
          <div className="flex flex-col items-center gap-1">
            <div className="scroll-arrow"></div>
            <div className="scroll-arrow delay-1"></div>
            <div className="scroll-arrow delay-2"></div>
          </div>
        </div>
      </section>

      {/* 機能セクション */}
      <section id="features" className="py-24 relative z-[1]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 scroll-fade-in">
            <h2 className="text-[clamp(34px,5vw,50px)] font-extrabold text-text-heading mb-5">
              TRPGに特化した音声認識
            </h2>
            <p className="text-lg text-text-sub max-w-xl mx-auto leading-relaxed">
              TRPGならではの用語や会話も正確に認識。<br />
              セッションの記録をもっと手軽に。
            </p>
          </div>

          {/* 機能一覧: 縦並びの横型カードで視線を一方向に */}
          <div className="max-w-2xl mx-auto space-y-5">
            {[
              {
                icon: <Brain className="w-8 h-8 text-white" />,
                title: '高精度音声認識',
                description: 'TRPG用語やキャラクター名を正確に認識。ダイスの目も聞き逃しません。',
              },
              {
                icon: <Clock className="w-8 h-8 text-white" />,
                title: 'タイムスタンプ付き',
                description: '発話時間を自動で記録。あの場面をすぐに振り返れます。',
              },
              {
                icon: <Shield className="w-8 h-8 text-white" />,
                title: 'プライバシー保護',
                description: '音声ファイルは処理後に自動削除。大切なセッション内容を外部に残しません。',
              },
              {
                icon: <Zap className="w-8 h-8 text-white" />,
                title: '処理速度',
                description: '3時間の録音を約15分で書き起こし。',
              },
              {
                icon: <Download className="w-8 h-8 text-white" />,
                title: '多形式出力',
                description: 'TXT・JSON・HTML形式で出力。お好みの形式で保存できます。',
              },
            ].map((feature, index) => (
              <Card key={index} className="flex items-center gap-6 scroll-fade-in">
                <div className="w-16 h-16 rounded-2xl bg-accent flex items-center justify-center flex-shrink-0">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-text-heading mb-1">{feature.title}</h3>
                  <p className="text-sm text-text-sub leading-relaxed">{feature.description}</p>
                </div>
              </Card>
            ))}
          </div>

          {/* 詳細ガイドへ（話者分離・テキスト再生・テキスト編集の実際の動き） */}
          <div className="text-center mt-10 scroll-fade-in">
            <Button variant="secondary" onClick={() => navigate('/guide')}>
              <span>機能ガイドを見る</span>
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* プライバシー安心セクション（外部AIへの不安を先回りで解消） */}
      <section id="privacy" className="py-24 relative z-[1]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="rounded-panel border border-accent/20 bg-accent/5 p-8 md:p-12 scroll-fade-in">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-accent mb-5">
                <ShieldCheck className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-[clamp(26px,4vw,40px)] font-extrabold text-text-heading mb-4">
                <span className="inline-block">シナリオも音声も、</span><span className="inline-block">外には出しません</span>
              </h2>
              <p className="text-text-sub leading-relaxed">
                <span className="font-bold text-text-heading">「AIって、ChatGPTやGeminiのこと？」</span>
                <br />
                いいえ。otomochi は外部の生成AIサービスを使いません。otomochi 自身が管理するモデルだけで処理し、あなたのデータを学習に使うこともありません。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  icon: <ShieldCheck className="w-6 h-6 text-white" />,
                  title: '外部AIに送りません',
                  description: 'ChatGPTやGeminiなど、第三者の生成AIサービスへデータを渡したり共有したりしません。',
                },
                {
                  icon: <Server className="w-6 h-6 text-white" />,
                  title: '自前のモデルで処理',
                  description: 'otomochiが自分で保持・管理するモデルだけで、書き起こしと用語の補正を行います。',
                },
                {
                  icon: <Lock className="w-6 h-6 text-white" />,
                  title: '学習に使いません',
                  description: 'お預かりした音声・テキストを、AIの学習や再学習に利用することは一切ありません。',
                },
                {
                  icon: <Trash2 className="w-6 h-6 text-white" />,
                  title: '処理後に自動削除',
                  description: '処理に使った音声は自動的に削除します（無制限プランのみ、聞き直し用に7日間だけ保持）。',
                },
              ].map((point, index) => (
                <Card key={index} className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-accent flex items-center justify-center flex-shrink-0">
                    {point.icon}
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-text-heading mb-1">{point.title}</h3>
                    <p className="text-sm text-text-sub leading-relaxed">{point.description}</p>
                  </div>
                </Card>
              ))}
            </div>

            <p className="text-center text-sm text-text-sub mt-8">
              データの取り扱いについては
              <button
                type="button"
                onClick={() => navigate('/faq')}
                className="mx-1 text-accent hover:text-accent-hover font-medium underline underline-offset-2"
              >
                よくある質問
              </button>
              でも詳しくご案内しています。
            </p>
          </div>
        </div>
      </section>

      {/* 料金プランセクション */}
      <section id="pricing" className="py-24 relative z-[1] bg-gradient-to-b from-background/50 to-transparent">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 scroll-fade-in">
            <h2 className="text-[clamp(34px,5vw,50px)] font-extrabold text-text-heading mb-5">
              シンプルな料金プラン
            </h2>
            <p className="text-lg text-text-sub max-w-xl mx-auto leading-relaxed">
              あなたのセッション頻度に合わせて選べます。いつでもプラン変更可能。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-7">
            {[
              {
                name: '無料プラン',
                price: '¥0',
                description: '月3回・合計15分',
                icon: <FileAudio className="w-7 h-7 text-white" />,
                color: '#3b82f6', // ビビッドブルー
                // 各プランの上限は「1回あたり」ではなく月間合計（FREE_PLAN_HOURS=0.25＝15分ほか）。
                // 1回あたりの長さはバックエンドで制限しておらず、実質の上限はファイルサイズ2GB。
                // 実在しない「1回最大◯時間」を掲げないよう、合計＋2GB で表記を統一する。
                features: ['月3回まで利用', '月合計15分まで', 'タイムスタンプ付き'],
                cta: '無料で試す',
                popular: false,
              },
              {
                name: 'ライトプラン',
                price: '¥500',
                description: '月3回・合計9時間',
                icon: <Sparkles className="w-7 h-7 text-white" />,
                color: '#059669', // エメラルドグリーン
                features: ['月3回・合計9時間まで', '1回の長さ制限なし（最大2GB）', 'タイムスタンプ付き'],
                cta: 'ライトプランで始める',
                popular: false,
              },
              {
                name: 'スタンダード',
                price: '¥1,000',
                description: '月6回・合計18時間',
                icon: <Star className="w-7 h-7 text-white" />,
                color: '#ef6c43', // 鮮やかなコーラル（ブランド＝人気プラン）
                features: ['月6回・合計18時間まで', '1回の長さ制限なし（最大2GB）', '話者分離'],
                cta: 'スタンダードで始める',
                popular: true,
              },
              {
                name: '無制限プラン',
                price: '¥4,000',
                description: '回数・時間とも無制限',
                icon: <Zap className="w-7 h-7 text-white" />,
                color: '#8b5cf6', // 鮮やかなバイオレット
                features: ['書き起こし放題（回数・時間の上限なし）', '1回の長さ制限なし（最大2GB）', 'すべての機能', 'テキスト再生機能'],
                cta: '無制限プランで始める',
                popular: false,
              },
            ].map((plan, index) => (
              <Card
                key={index}
                className={`text-center scroll-fade-in relative ${
                  plan.popular
                    ? 'border-2 border-primary lg:scale-105 shadow-xl'
                    : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white px-6 py-2.5 rounded-3xl text-sm font-semibold flex items-center gap-2 shadow-lg">
                    <Star className="w-4 h-4" />
                    <span>人気No.1</span>
                  </div>
                )}
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: plan.color }}>
                  {plan.icon}
                </div>
                <h3 className="text-xl font-bold text-text-heading mb-4">{plan.name}</h3>
                <div className="text-4xl font-extrabold text-text-heading mb-2">
                  {plan.price}
                  {plan.price !== '¥0' && <span className="text-base font-medium text-text-sub">/月</span>}
                </div>
                <p className="text-sm text-text-sub mb-7">{plan.description}</p>
                <ul className="text-left mb-7 space-y-2.5">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-text-sub">
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => navigate(isLoggedIn ? '/billing' : '/login')}
                  className="w-full"
                >
                  {isLoggedIn ? 'プランを見る' : plan.cta}
                </Button>
              </Card>
            ))}
          </div>

          {/* ワンショットプラン */}
          <Card className="flex items-center justify-center gap-10 flex-wrap mt-16 scroll-fade-in">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#ec4899' }}>
              <Clock className="w-10 h-10 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-text-heading">ワンショット利用</h3>
              <p className="text-sm text-text-sub mt-1">必要な時だけ利用したい方に</p>
            </div>
            <div className="flex gap-10 flex-wrap justify-center">
              <div className="text-center">
                <div className="text-4xl font-extrabold text-primary">
                  ¥120<span className="text-lg">/時間</span>
                </div>
                <p className="text-xs text-text-sub mt-1">時間の追加（無期限）</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-extrabold text-primary">
                  ¥100<span className="text-lg">/回</span>
                </div>
                <p className="text-xs text-text-sub mt-1">回数の追加（ライト・スタンダード向け・無期限）</p>
              </div>
            </div>
          </Card>

          {/* プラン比較・変更/解約の扱いまで踏み込んだ詳細ページへ */}
          <div className="text-center mt-10 scroll-fade-in">
            <Button variant="secondary" onClick={() => navigate('/pricing')}>
              <span>料金プランを詳しく見る</span>
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Why otomochiセクション */}
      <section id="about" className="py-24 relative z-[1]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12 scroll-fade-in">
            <h2 className="text-[clamp(34px,5vw,50px)] font-extrabold text-text-heading mb-5">
              なぜotomochi？
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                icon: <Sparkles className="w-6 h-6 text-white" />,
                title: 'TRPG専用設計',
                description: '一般的な音声認識とは違い、TRPGの用語やシチュエーションに合わせて調整しています。',
              },
              {
                icon: <Shield className="w-6 h-6 text-white" />,
                title: 'プライバシー重視',
                description: '音声ファイルは処理後に自動削除。セッション内容を外部に残しません。',
              },
              {
                icon: <Zap className="w-6 h-6 text-white" />,
                title: 'しっかり速い',
                description: '3時間の録音を約15分で書き起こし。待ち時間を気にせず使えます。',
              },
            ].map((item, index) => (
              <div key={index} className="flex gap-6 p-6 rounded-2xl bg-white shadow-card scroll-fade-in">
                <div className="w-14 h-14 rounded-xl bg-accent flex items-center justify-center flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-text-heading mb-2">{item.title}</h3>
                  <p className="text-text-sub leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA セクション */}
      <section className="py-24 relative z-[1]">
        <div className="max-w-4xl mx-auto px-6">
          <Card className="text-center py-20 px-8 md:px-16 scroll-fade-in">
            <h2 className="text-[clamp(30px,4vw,44px)] font-extrabold text-text-heading mb-6">
              セッション記録を<br />もっと手軽に
            </h2>
            <p className="text-lg text-text-sub mb-11 leading-relaxed">
              無料プランで気軽にお試しいただけます。<br />
              あなたのTRPGセッションを、テキストで残してみませんか。
            </p>
            <div className="flex items-center justify-center gap-6 flex-wrap">
              {isLoggedIn ? (
                // ログイン済み: 「無料で始める」「ログイン」は不要なため、ダッシュボード導線に集約。
                <Button
                  size="lg"
                  onClick={() => navigate('/dashboard')}
                  className="hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                >
                  <span>ダッシュボードへ</span>
                  <ArrowRight className="w-5 h-5" />
                </Button>
              ) : (
                <>
                  <Button
                    size="lg"
                    onClick={() => navigate('/login')}
                    className="hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                  >
                    <span>無料で始める</span>
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={() => navigate('/login')}
                  >
                    ログイン
                  </Button>
                </>
              )}
            </div>
          </Card>
        </div>
      </section>

      {/* 運営者の Misskey サーバー紹介（フッター直前）。
          本ページは Layout を経由せず Footer を直接置くため、バナーもここで直接描画する。 */}
      <PromoBanner />

      {/* フッター */}
      <Footer />

      {/* 最小限のスタイル: keyframeアニメーションとスクロール演出のみ */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }

        .logo-float {
          animation: float 4s ease-in-out infinite;
        }

        @keyframes arrow-bounce {
          0%, 100% { opacity: 0.3; transform: rotate(45deg) translateY(0); }
          50% { opacity: 1; transform: rotate(45deg) translateY(4px); }
        }

        .scroll-arrow {
          width: 12px;
          height: 12px;
          border-right: 2px solid #b35530;
          border-bottom: 2px solid #b35530;
          transform: rotate(45deg);
          animation: arrow-bounce 1.5s ease-in-out infinite;
        }

        .scroll-arrow.delay-1 { animation-delay: 0.15s; }
        .scroll-arrow.delay-2 { animation-delay: 0.3s; }

        .scroll-fade-in {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }

        .scroll-fade-in.visible {
          opacity: 1;
          transform: translateY(0);
        }

        @media (prefers-reduced-motion: reduce) {
          .logo-float {
            animation: none;
          }

          .scroll-arrow {
            animation: none;
            opacity: 0.6;
          }

          .scroll-fade-in {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }
      `}</style>
    </>
  );
};

export default HomePage;
